/**
 * Created by Ning Jiang on 2021-06-29.
 */

import {ContinueScreen} from "../../core/screen/ContinueScreen";
import {Layers, TombstoneRipLayers} from "../view/TombstoneRipLayers";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {TombstoneRip} from "../TombstoneRip";
import {Translation} from "../../core/utils/Translation";
import {gsap, TimelineLite} from "gsap";
import {TombstoneRipGameMode} from "../gameflow/TombstoneRipGameMode";
import {BigWinCountUp} from "../winpresentation/presentations/bigwin/BigWinCountUp";
import {TombstoneRipSound} from "../sound/TombstoneRipSound";
import {TombstoneRipGameConfig} from "../TombstoneRipGameConfig";
import {IWinRatioConfig} from "../../core/config/WinRatio";
import {BelowStakeWinRestriction} from "../server/BelowStakeWinRestriction";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";

export class FreespinOutro extends ContinueScreen {

    private _bg:PIXI.Graphics;
    private _image:PIXI.Sprite;
    private _text:PIXI.Sprite;
    private _nothing:PIXI.Sprite;
    private _countUp:BigWinCountUp;
    private _played:PIXI.Text;

    constructor() {
        super(TombstoneRipLayers.getLayer(Layers.SCREENS)!);

        this._bg = new PIXI.Graphics();
        this._bg.beginFill(0x000000, 0.8);
        this._bg.drawShape(new PIXI.Rectangle(-840, -840, 1680, 1680));
        this._bg.endFill();
        this._bg.position.set(360, 360);
        this._container.addChild(this._bg);

        this._image = new PIXI.Sprite();
        this._image.anchor.set(0.5);
        this._image.position.set(360, 360);
        this._container.addChild(this._image);

        this._text = new PIXI.Sprite();
        this._text.anchor.set(0.5);
        this._container.addChild(this._text);

        this._nothing = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures("fsoutro/nothing")[0]);
        this._nothing.anchor.set(0.5);
        this._nothing.position.set(330, 300);
        this._container.addChild(this._nothing);

        this._played = new PIXI.Text("", new PIXI.TextStyle({
            fill : 0xffffff,
            fontFamily : OpenSans.FAMILY,
            wordWrap : false,
            fontWeight : FontWeight.BOLD,
            fontSize : 30
        }));
        this._played.anchor.set(0.5, 0.5);
        this._played.position.set(360, 560);
        this._container.addChild(this._played);

        this._countUp = new BigWinCountUp(TombstoneRipLayers.getLayer(Layers.SCREENS)!, 300);
    }

    public show(onContinueCallBack:() => void) {
        this.reset();

        const belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowStake();

        SlotGame.keypadPlugin.setWin(TombstoneRip.model.parsedGameData.totalWin, true, belowStakeWinRestriction);
        SlotGame.keypadPlugin.hide();

        const data = TombstoneRip.model.parsedGameData;
        let imageName = `fsoutro/outro_${data.mode}`;
        if(!!SlotGame.apiPlugin.gameClientConfiguration.useCensoredGfxUS && data.mode === GameMode.FREESPIN_SUPER) {
            imageName += `_WV`;
        }
        this._image.texture = TombstoneRip.assetLoader.getTextures(imageName)[0];
        this._text.texture = TombstoneRip.assetLoader.getTextures(this.getTextTextureName())[0];
        if(data.mode === TombstoneRipGameMode.FREESPIN_SUPER) {
            this._text.position.set(360, 134);
            this._text.scale.set(0.8);
        } else {
            this._text.position.set(582, 360);
            this._text.scale.set(1);
        }
        const spinPlayed = data.numberOfFreespinsPlayed;
        this._played.text = Translation.translate("Spins played: ${spinPlayed}").replace("${spinPlayed}", spinPlayed.toString());
        const countUpDuration = belowStakeWinRestriction ? 0.01 : 2;

        const tl = new TimelineLite();
        tl.add(() => {this.visible = true});
        tl.add(() => this.playEndSound());
        tl.add(gsap.to(this._bg, {duration : 0.4, alpha : 1}), 0);
        tl.add(gsap.to(this._image, {duration : 0.4, alpha : 1}), 0.2);
        tl.add(gsap.to(this._text, {duration : 0.4, alpha : 1}), 0.2);
        if(data.totalWin > 0) {
            tl.add([
                this._countUp.getCountUpAnimation(0, data.totalWin, countUpDuration),
                gsap.fromTo(this._countUp.scale,
                    {x : 0.8, y : 0.8},
                    {x : 1, y : 1, duration : countUpDuration}
                )
            ], 0.2);
            if(!belowStakeWinRestriction) {
                tl.add(() => {
                    TombstoneRipSound.soundPlayer.playTrack({
                        name : TombstoneRipSound.INITIAL_COUNT_UP_COUNTER,
                        repeat : 100
                    })
                }, 0.2);
            }
        } else {
            tl.add(gsap.to(this._nothing, {duration : 0.4, alpha : 1}), 0.2);
        }
        tl.add(gsap.to(this._played, {duration : 0.3, alpha : 1}), 0.4);
        tl.add(() => {
            if(data.totalWin > 0 && !belowStakeWinRestriction) {
                TombstoneRipSound.fadeAndStopSound({
                    name : TombstoneRipSound.INITIAL_COUNT_UP_COUNTER,
                    to : 0,
                    duration : 100
                });
                TombstoneRipSound.soundPlayer.playTrack({name : this.getCountUpEndSoundName()})
            }
            super.show(onContinueCallBack);
        });
    }

    private getCountUpEndSoundName():string {
        const data = TombstoneRip.model.parsedGameData;
        const totalWinRatio = data.totalWin / data.playedBetValue;
        let ratioConfig:IWinRatioConfig;
        for(let i:number = TombstoneRipGameConfig.WIN_RATIOS.length - 1; i >= 0; i--) {
            const config = TombstoneRipGameConfig.WIN_RATIOS[i];
            if(totalWinRatio >= config.ratio) {
                ratioConfig = config;
                break;
            }
        }

        const name = ratioConfig!.name!.includes("BIG") ? "LARGE" : ratioConfig!.name;
        return (<any>TombstoneRipSound)[`INITIAL_COUNT_UP_END_${name}`];
    }

    private playEndSound():void {
        TombstoneRipSound.soundPlayer.playTrack({
            name : TombstoneRip.model.parsedGameData.mode === TombstoneRipGameMode.FREESPIN_SUPER ?
                TombstoneRipSound.SUPER_FS_END : TombstoneRipSound.FS_END
        });
        TombstoneRipSound.switchMusic(
            {
                name : TombstoneRipSound.MAIN_GAME_MUSIC,
                volume : 1
            },
            undefined,
            {name : "", to : 0, duration : 300}
        );
    }

    private getTextTextureName():string {
        // return "fsoutro/FS_text1";
        const data = TombstoneRip.model.parsedGameData;
        const bonusBuyPrice = data.playedBetValue * ((data.reelsEnteringFreespin && data.reelsEnteringFreespin[4].includes("S2")) ? 3000 : 66);
        const winRatio = TombstoneRip.model.parsedGameData.totalWin / bonusBuyPrice;
        if(data.mode === TombstoneRipGameMode.FREESPIN_SUPER) {
            if(winRatio > 3) {
                return "fsoutro/SuperFS_text3";
            }
            if(winRatio > 0.2) {
                return "fsoutro/SuperFS_text2";
            } else {
                return "fsoutro/SuperFS_text1";
            }
        } else {
            if(winRatio > 4) {
                return "fsoutro/FS_text3";
            }
            if(winRatio > 0.1) {
                return "fsoutro/FS_text2";
            } else {
                return "fsoutro/FS_text1";
            }
        }
    }

    private reset():void {
        this._bg.alpha = 0;
        this._image.alpha = 0;
        this._text.alpha = 0;
        this._countUp.reset();
        this._countUp.alpha = 1;
        this._played.alpha = 0;
        this._nothing.alpha = 0;
    }

    protected close():TimelineLite {
        const tl = super.close();
        tl.add(() => {
            this._countUp.reset();
            TombstoneRip.background.updateEnvironment(TombstoneRip.model.parsedGameData.nextMode);
            TombstoneRip.multiplier.resetAfterFS();
            TombstoneRip.reels.forEach(reel => {
                reel.resetSuperMidSymbol();
            });
            TombstoneRip.superFsPop.reset();
            TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.FS_BUTTON});
        }, 0);
        return tl;
    }
}